import {useCallback, useRef, useState} from 'react';
import {
  getDatesetProcessingCompletedAlert,
  getDatesetProcessingExceptionAlert,
} from '../../../api/UserManagementService';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {AxiosResponse} from 'axios';

interface IDatasetAlertModal {
  workspaceId: string;
  workspaceCode: string;
  workspaceName: string;
  triggeredOn: string;
  custodian: string;
  dataset: string;
}

type AlertType =
  | 'DatasetCompletedProcessing'
  | 'DataHasProcessingExceptions'
  | 'NewInvoiceAvailable';

interface UseDatasetAlertReturn {
  loading: boolean;
  datasetAlerts: IDatasetAlertModal[];
  getDatasetAlerts: (
    type: AlertType,
    reportType: string,
    projectId: string
  ) => void;
  reset(): void;
}

const useDatasetAlert = (): UseDatasetAlertReturn => {
  const [alerts, setAlerts] = useState({
    loading: true,
    datasetAlerts: [],
    error: undefined,
  });
  const reportTypeRef = useRef<string>();
  const alertTypeRef = useRef<string>();

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const updateData = useCallback((response?: AxiosResponse) => {
    setAlerts({
      loading: false,
      datasetAlerts: response
        ? response.data?.data?.map(
            (data: IDatasetAlertModal, index: number) => {
              return {
                userAlertId: index,
                alertType: alertTypeRef.current,
                reportType: reportTypeRef.current,
                datasets: data.dataset ? [data.dataset] : [],
                ...data,
              };
            }
          )
        : [],
      error: undefined,
    });
  }, []);

  const datasetCompletedAlert = useCallback(
    (projectId: string) => {
      getDatesetProcessingCompletedAlert(clientId, projectId)
        .then((response: AxiosResponse) => {
          updateData(response);
        })
        .catch(() => {
          updateData();
        });
    },
    [clientId, updateData]
  );

  const datasetExceptionAlert = useCallback(
    (projectId: string) => {
      getDatesetProcessingExceptionAlert(clientId, projectId)
        .then((response: AxiosResponse) => {
          updateData(response);
        })
        .catch(() => {
          updateData();
        });
    },
    [clientId, updateData]
  );

  const getDatasetAlerts = useCallback(
    (type: AlertType, reportType: string, projectId: string) => {
      if (clientId && projectId) {
        alertTypeRef.current = type;
        reportTypeRef.current = reportType;
        if (type === 'DatasetCompletedProcessing') {
          datasetCompletedAlert(projectId);
        } else {
          datasetExceptionAlert(projectId);
        }
      }
    },
    [clientId, datasetCompletedAlert, datasetExceptionAlert]
  );

  const reset = useCallback(() => {
    alertTypeRef.current = undefined;
    reportTypeRef.current = undefined;
    setTimeout(
      () =>
        setAlerts({
          loading: true,
          datasetAlerts: [],
          error: undefined,
        }),
      100
    );
  }, []);

  return {
    ...alerts,
    getDatasetAlerts,
    reset,
  };
};

export {useDatasetAlert};
